import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, MapPin, Footprints, ChevronRight, Sparkles, Store, FlaskConical } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { DetailGallery } from "@/components/DetailGallery";
import { Rating } from "@/components/Rating";
import { ReviewsSection } from "@/components/ReviewsSection";
import { StatusBadge } from "@/components/StatusBadge";
import { MapPreview } from "@/components/MapPreview";
import { formatDistance } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";

export default function EstablishmentDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const [spot, setSpot] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setSpot(null);
    setNotFound(false);
    api
      .get(`/establishments/${slug}`)
      .then((r) => setSpot(r.data))
      .catch(() => setNotFound(true));
    api.post("/views", { item_type: "etablissement", item_slug: slug }).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <main className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="spot-not-found">
        <h1 className="text-2xl font-bold text-slate-900">{t("Établissement introuvable")}</h1>
        <Link to="/explorer" data-testid="back-explorer-link" className="mt-6 inline-block rounded-full bg-[#2563EB] px-6 py-3 text-sm font-semibold text-white">
          {t("Explorer les spots")}
        </Link>
      </main>
    );
  }

  if (!spot) {
    return (
      <main className="mx-auto max-w-[1800px] px-4 py-10 sm:px-6">
        <div className="h-96 animate-pulse rounded-3xl bg-white" />
      </main>
    );
  }

  return (
    <main data-testid="spot-detail-page" className="mx-auto max-w-[1800px] px-4 pb-16 pt-6 sm:px-6 lg:px-10">
      <Seo title={`${spot.name} — GoSpot`} siteName="GoSpot" description={spot.description || `${spot.name} — ${spot.category_label} à ${spot.neighborhood}, Québec.`} />

      <nav className="mb-5 flex items-center gap-1.5 text-sm text-slate-400" data-testid="breadcrumb">
        <Link to="/" className="transition hover:text-[#2563EB]">{t("Accueil")}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <Link to="/explorer" className="transition hover:text-[#2563EB]">{t("Explorer")}</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="truncate font-medium text-slate-700">{spot.name}</span>
      </nav>

      <DetailGallery images={spot.gallery} badge={spot.badge} item={spot} itemType="etablissement" />

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_360px]">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">{spot.name}</h1>
            <StatusBadge badge={spot.badge} soft />
            {spot.is_demo && (
              <span data-testid="demo-badge-detail" className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-600 ring-1 ring-amber-200">
                <FlaskConical className="h-3.5 w-3.5" />
                {t("Établissement de démonstration")}
              </span>
            )}
          </div>
          <p className="mt-2 text-base text-slate-500">
            {t(spot.category_label)} • {spot.neighborhood}, Québec
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-4">
            <Rating value={spot.rating} count={spot.reviews_count} />
            <span className="flex items-center gap-1 text-sm text-slate-400">
              <Footprints className="h-4 w-4" />
              {formatDistance(spot.distance_m, lang)}
            </span>
            {spot.is_open && (
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                {t("Ouvert maintenant")}
              </span>
            )}
          </div>

          <div className="mt-8">
            {spot.promotions?.length > 0 && (
              <div className="mb-6 rounded-3xl border border-blue-100 bg-[#EDF3FE] p-5" data-testid="spot-promo-banner">
                {spot.promotions.map((p, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-[#2563EB]" />
                    <div>
                      <p className="text-sm font-bold text-slate-900">{p.title}</p>
                      {p.description && <p className="mt-0.5 text-sm text-slate-600">{p.description}</p>}
                      {p.valid_until && <p className="mt-1 text-xs text-slate-400">{lang === "en" ? `Valid until ${p.valid_until}` : `Valide jusqu'au ${p.valid_until}`}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <h2 className="text-lg font-bold text-slate-900">{t("À propos")}</h2>
            <p className="mt-3 leading-relaxed text-slate-600">{spot.description}</p>
            {spot.tags?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2.5">
                {spot.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-[#EFF6FF] px-4 py-1.5 text-xs font-semibold text-[#2563EB]">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="mt-10">
            <ReviewsSection itemType="etablissement" itemSlug={spot.slug} />
          </div>
        </div>

        <aside className="space-y-5 self-start lg:sticky lg:top-24">
          <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="spot-info-card">
            <h2 className="text-base font-bold text-slate-900">{t("Informations")}</h2>
            <div className="mt-4 space-y-3 text-sm">
              <p className="flex items-center gap-2.5 text-slate-600">
                <MapPin className="h-4 w-4 shrink-0 text-[#2563EB]" />
                {spot.address || `${spot.neighborhood}, Québec, QC`}
              </p>
              {spot.hours_detail ? (
                <div className="space-y-1.5">
                  <p className="flex items-center gap-2.5 text-slate-600">
                    <Clock className="h-4 w-4 shrink-0 text-[#2563EB]" />
                    <span className="font-semibold text-slate-900">{t("Horaires")}</span>
                  </p>
                  <div className="pl-7">
                    {spot.hours_detail.map((h) => (
                      <p key={h.day} className="flex items-center justify-between text-xs text-slate-500">
                        <span>{t(h.day)}</span>
                        <span className={h.closed || h.value === "Fermé" ? "text-slate-400" : "font-medium text-slate-700"}>{t(h.value)}</span>
                      </p>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="flex items-center gap-2.5 text-slate-600">
                  <Clock className="h-4 w-4 shrink-0 text-[#2563EB]" />
                  {spot.hours || t("Horaires variables")}
                </p>
              )}
              {spot.price_from && (
                <p className="flex items-center gap-2.5 font-semibold text-[#2563EB]">
                  <Sparkles className="h-4 w-4 shrink-0" />
                  {lang === "en" ? `From $${spot.price_from} per night` : `À partir de ${spot.price_from} $ par nuit`}
                </p>
              )}
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm" data-testid="spot-map-card">
            <h2 className="px-5 pb-3 pt-4 text-base font-bold text-slate-900">{t("Emplacement")}</h2>
            <div className="px-3 pb-3">
              <MapPreview markers={[spot]} showButton={false} />
            </div>
          </div>

          <div className="rounded-3xl border border-blue-100 bg-[#EDF3FE] p-5" data-testid="spot-owner-card">
            <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <Store className="h-5 w-5 text-[#2563EB]" />
              {t("C’est ton établissement ?")}
            </h2>
            <p className="mt-2 text-sm text-slate-600">{t("Gère ta page, publie tes événements et suis tes statistiques.")}</p>
            <button
              type="button"
              data-testid="spot-owner-button"
              onClick={() => (user ? navigate("/pro/etablissement") : navigate("/connexion"))}
              className="mt-4 w-full rounded-full bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8]"
            >
              {t("Accéder à mon espace Pro")}
            </button>
          </div>
        </aside>
      </div>
    </main>
  );
}
